"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight, IdCard, X, Headphones } from "@/components/icons";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/";
import { CONTACT_SALES_URL, FREE_TRIAL_URL, FREE_TRIAL_LABEL, ROUTES } from "@/lib/constants";
import { MAINTENANCE_PATH } from "@/lib/maintenance";
import { useMaintenanceMode } from "@/hooks/use-maintenance-mode";
import { useAffiliateRef } from "@/hooks/use-affiliate-ref";

const MotionDiv = motion.div;

type IconComponent = React.ComponentType<{
  size?: number;
  style?: React.CSSProperties;
  "aria-hidden"?: "true";
}>;

interface Perk {
  icon: IconComponent;
  label: string;
}

/* The closing band's reassurances. These live HERE, not in each caller: all
   three callers passed this identical array, so "the CTA" was only global as
   far as its markup — the copy could drift page to page. A caller can still
   override for a page that genuinely promises something else. */
const DEFAULT_PERKS: Perk[] = [
  { icon: IdCard, label: "KYC required to start" },
  { icon: X, label: "Cancel any time, no penalties" },
  { icon: Headphones, label: "Premium live support included" },
];

interface PricingCtaSectionProps {
  id: string;
  /** Plain sentence — see the heading note below; don't pass <br /> or an accent span. */
  heading: React.ReactNode;
  body?: React.ReactNode;
  /** Pass `[]` on a page where none of the trial reassurances apply (careers). */
  perks?: Perk[];
  /** Replaces the trial + sales pair for a page whose conversion action is
   *  genuinely something else — a browser store listing, a repo, open roles.
   *  The band, headline scale and perk row stay identical either way, which is
   *  the whole point of routing those pages through here rather than letting
   *  each one hand-roll a closing section. */
  actions?: React.ReactNode;
  /** Small print under the perks — a help link, a licence note. */
  footnote?: React.ReactNode;
}

export default function PricingCtaSection({
  id,
  heading,
  body,
  perks = DEFAULT_PERKS,
  actions,
  footnote,
}: PricingCtaSectionProps) {
  const prefersReducedMotion = useReducedMotion();
  const maintenanceMode = useMaintenanceMode();
  const { ref: affiliateRef } = useAffiliateRef();
  const isBuyMode = !!affiliateRef;
  const trialHref = maintenanceMode
    ? MAINTENANCE_PATH
    : isBuyMode
      ? ROUTES.pricing
      : FREE_TRIAL_URL;

  const fadeUp = (delay = 0) => ({
    initial: prefersReducedMotion ? {} : { opacity: 0, y: 24 },
    whileInView: prefersReducedMotion ? {} : { opacity: 1, y: 0 },
    viewport: scrollRevealViewport,
    transition: { duration: 0.55, delay },
  });

  return (
    /* `.cta-wash` lives in app/cta-wash.css — a flat base plus three
       radial-gradient layers painted straight onto the section, with every
       colour exposed there as a variable. That is why there is no overlay
       element here and nothing inside needs `relative` or a z-index; the old
       absolutely positioned blue radial is gone with it. Retune the wash in that
       file; this component owns the layout only. */
    <section className="full-bleed cta-wash py-section" aria-labelledby={id}>
      <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
        <MotionDiv {...fadeUp(0)}>
          {/* One line, one colour. The headline used to be split by a <br /> with
              the second half in the accent colour; at these sizes the sentence
              fits on a single line up to `lg` and reads far stronger whole, and
              solid foreground ink is what carries against the warm ground.
              max-w-3xl, not 2xl: the longest of these headlines is ~700px set at
              text-5xl, which 2xl (672px) would wrap for the sake of 30px. */}
          <h2
            id={id}
            className="text-foreground mx-auto mb-5 max-w-3xl text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl"
          >
            {heading}
          </h2>
        </MotionDiv>

        <MotionDiv {...fadeUp(0.14)}>
          <p className="text-muted-foreground mx-auto mb-8 max-w-xl text-base leading-relaxed">
            {body}
          </p>
        </MotionDiv>

        <MotionDiv {...fadeUp(0.2)} className="mb-8 flex flex-wrap justify-center gap-3">
          {actions ?? (
            <>
              <Link
                href={trialHref}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  buttonVariants({ variant: "default", size: "lg" }),
                  /* Light-only glow. On light the primary is blue (--primary
                     #2563eb) and a soft cast of its own colour lifts it off the
                     warm ground, as in the mockup. On dark the primary is
                     near-white — a white halo there just looks like a blur, so
                     it stays off. */
                  "light:shadow-[0_10px_28px_-8px_rgb(var(--accent-blue-deep)/0.55)]"
                )}
              >
                {isBuyMode ? "Buy Now" : FREE_TRIAL_LABEL}
                <ArrowRight size={16} className="ml-2" aria-hidden="true" />
              </Link>
              <Link
                href={CONTACT_SALES_URL}
                className={cn(buttonVariants({ variant: "outline", size: "lg" }))}
              >
                Talk to Sales
              </Link>
            </>
          )}
        </MotionDiv>

        {/* Perks */}
        <MotionDiv {...fadeUp(0.26)} className="flex flex-wrap items-center justify-center gap-6">
          {perks.map(({ icon: Icon, label }, i) => (
            <div
              key={label}
              className={cn(
                "text-muted-foreground items-center gap-2 text-sm",
                i === 0 ? "flex" : "hidden md:flex"
              )}
            >
              <Icon
                size={14}
                style={{ color: "color-mix(in srgb, var(--foreground) 50%, transparent)" }}
                aria-hidden="true"
              />
              {label}
            </div>
          ))}
        </MotionDiv>

        {footnote && (
          <MotionDiv {...fadeUp(0.32)}>
            <p className="text-muted-foreground mt-8 text-sm">{footnote}</p>
          </MotionDiv>
        )}
      </div>
    </section>
  );
}
